if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      this.form.querySelector('[name=id]').disabled = false;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.submitButton = this.querySelector('[type="submit"]');
      if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

      this.hideErrors = this.dataset.hideErrors === 'true';
    }

    onSubmitHandler(evt) {
      evt.preventDefault();
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

      this.handleErrorMessage();

      this.submitButton.setAttribute('aria-disabled', true);
      this.submitButton.classList.add('loading');
      this.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(this.form);
      if (this.cart) {
        formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        this.cart.setActiveElement(document.activeElement);
      }
      config.body = formData;

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            publish(PUB_SUB_EVENTS.cartError, {source: 'product-form', productVariantId: formData.get('id'), errors: response.description, message: response.message});
            this.handleErrorMessage(response.description);

            // Check if this is a partial quantity warning (422 status with availability message)
            if (response.status === 422 && response.message && response.message.toLowerCase().includes('only') && response.message.toLowerCase().includes('added to your cart due to availability')) {
              // Show warning popup for partial quantity addition
              if (window.addToCartPopup) {
                const productName = this.getProductName();
                const productPrice = this.getProductPrice();
                window.addToCartPopup.showWarning(response.message, productName, productPrice);
              }
            } else {
              // Show error popup for actual errors
              if (window.addToCartPopup) {
                const productName = this.getProductName();
                let errorMessage = response.description || response.message || 'Unable to add item to cart';
                
                // Check if error contains max allowed inventory value - if so, don't show popup
                const maxInventoryValue = window.productData?.metafields?.custom?.max_allowed_inventory_per_cart?.value;
                if (maxInventoryValue && errorMessage.includes(maxInventoryValue.toString())) {
                  // Don't show popup for max inventory errors
                  return;
                }
                
                // Customize error message for common scenarios
                if (errorMessage.toLowerCase().includes('sold out') || errorMessage.toLowerCase().includes('out of stock')) {
                  errorMessage = 'Sorry, this item is out of stock';
                } else if (errorMessage.toLowerCase().includes('quantity') || errorMessage.toLowerCase().includes('available')) {
                  errorMessage = 'Sorry, the requested quantity is not available';
                }
                
                window.addToCartPopup.showError(errorMessage, productName);
              }
            }

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (!soldOutMessage) return;
            this.submitButton.setAttribute('aria-disabled', true);
            this.submitButton.querySelector('span').classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
            this.error = true;
            return;
          } else if (!this.cart) {
            // window.location = window.routes.cart_url;
            // Instead of redirecting to cart, show popup notification
            this.showAddToCartPopup(response);
            
            // Update cart count - fetch fresh data since response might not have item_count
            this.updateCartCountFromResponse(response);
            return;
          }

          if (!this.error) publish(PUB_SUB_EVENTS.cartUpdate, {source: 'product-form', productVariantId: formData.get('id')});
          this.error = false;
          
          // Show popup notification for successful add to cart
          this.showAddToCartPopup(response);
          
          // Update cart count - response should have item_count when cart drawer is present
          this.updateCartCountFromResponse(response);
          
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener('modalClosed', () => {
              setTimeout(() => { 
                this.cart.renderContents(response);
                // Also update cart form visibility for main cart page
                if (window.updateCartFormVisibility && response.items) {
                  window.updateCartFormVisibility(response.items);
                }
              });
            }, { once: true });
            quickAddModal.hide(true);
          } else {
            this.cart.renderContents(response);
            // Also update cart form visibility for main cart page
            if (window.updateCartFormVisibility && response.items) {
              window.updateCartFormVisibility(response.items);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
        });
    }

    handleErrorMessage(errorMessage = false) {
      if (this.hideErrors) return;

      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }

    showAddToCartPopup(response) {
      // Get product information directly from response or form
      let productName = 'Product';
      let productPrice = '';
      
      // Try to get from response first (most reliable)
      if (response && response.product_title) {
        productName = response.product_title;
      } else if (response && response.product) {
        productName = response.product.title || response.product.name || 'Product';
      } else {
        // Fallback to DOM - simple approach
        const titleElement = document.querySelector('h1, .product-title, [data-product-title]');
        if (titleElement) {
          productName = titleElement.textContent.trim();
        }
      }
      
      // Get price from response or DOM
      if (response && response.price) {
        productPrice = '$' + (response.price / 100).toFixed(2);
      } else {
        // Fallback to DOM
        const priceElement = document.querySelector('.price, .product-price, [data-price]');
        if (priceElement) {
          productPrice = priceElement.textContent.trim();
        }
      }
      
      // Show popup notification
      if (window.addToCartPopup) {
        window.addToCartPopup.show(productName, productPrice);
      }
    }

    getProductName() {
      // Try to get from form data first
      const productTitleInput = this.form.querySelector('[name="product_title"]');
      if (productTitleInput && productTitleInput.value) {
        return productTitleInput.value;
      }
      
      // Fallback to DOM - simple approach
      const titleElement = document.querySelector('h1, .product-title, [data-product-title]');
      if (titleElement) {
        return titleElement.textContent.trim();
      }
      
      return 'Product';
    }

    getProductPrice() {
      // Try to get from form data first
      const priceInput = this.form.querySelector('[name="price"]');
      if (priceInput && priceInput.value) {
        return '$' + (parseFloat(priceInput.value) / 100).toFixed(2);
      }
      
      // Fallback to DOM
      const priceElement = document.querySelector('.price, .product-price, [data-price]');
      if (priceElement) {
        return priceElement.textContent.trim();
      }
      
      return '';
    }

    updateCartCountFromResponse(response) {
      // For product page - handle quantity-based updates
      if (response && response.item_count !== undefined) {
        // Response has item_count, use it directly
        if (window.updateCartCount) {
          window.updateCartCount(response.item_count);
        }
      } else {
        // Response doesn't have item_count, fetch fresh cart data
        fetch('/cart.js')
          .then(response => response.json())
          .then(cart => {
            if (window.updateCartCount) {
              window.updateCartCount(cart.item_count);
            }
          })
          .catch(error => {
            console.error('Error fetching cart data:', error);
          });
      }
    }
  });
}
